import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiClient } from '../../../../utils/api-client';
import {
  Box,
  Container,
  CircularProgress,
  Alert
} from '@mui/material';
import RecruiterHeaderNew from '../../../../components/common/RecruiterHeaderNew';
import { BUILD_VERSION } from '../../../../utils/buildVersion';

// Import all profile components
import ProfileHeader from '../../../../components/profile/ProfileHeader';
import ProfileSummary from '../../../../components/profile/ProfileSummary';
import ProfileSkills from '../../../../components/profile/ProfileSkills';
import ProfileWorkExperience from '../../../../components/profile/ProfileWorkExperience';
import ProfileEducation from '../../../../components/profile/ProfileEducation';
import ProfileCertifications from '../../../../components/profile/ProfileCertifications';
import ProfileAwards from '../../../../components/profile/ProfileAwards';
import ProfileLanguages from '../../../../components/profile/ProfileLanguages';
import ProfilePreferredLocations from '../../../../components/profile/ProfilePreferredLocations';

interface CandidateProfile {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  current_title?: string;
  current_company?: string;
  current_location?: string;
  job_looking_for?: string;
  actively_looking_for_job: boolean;
  preferred_locations?: string[];
  total_experience_years?: number;
  skills?: any[];
  certifications?: any[];
  education_details?: any[];
  work_experience?: any[];
  summary?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  languages?: any[];
  awards?: any[];
  timezone?: string;
  profile_visibility?: string;
  created_at?: string;
  updated_at?: string;
}

export default function CandidatePublicProfile() {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCandidateProfile();
    }
  }, [id]);

  const fetchCandidateProfile = async () => {
    try {
      setLoading(true);
      console.log('Fetching profile for ID:', id);
      const response = await new ApiClient().get(`/candidates/${id}/profile`);
      console.log('API Response:', response);
      
      if (response.success && response.data) {
        console.log('Setting profile data:', response.data);
        setProfile(response.data);
      } else {
        console.error('API error:', response.error);
        setError(response.error || 'Failed to load candidate profile');
      }
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load candidate profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <RecruiterHeaderNew 
          onScheduleInterview={() => console.log('Schedule interview')}
          onCreatePosition={() => console.log('Create position')}
        />
        <main className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex-1 w-full">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Box>
        </main>
        <footer className="border-t border-gray-200 bg-white py-3 text-center text-xs text-gray-500">
          Build {BUILD_VERSION}
        </footer>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <RecruiterHeaderNew 
          onScheduleInterview={() => console.log('Schedule interview')}
          onCreatePosition={() => console.log('Create position')}
        />
        <main className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex-1 w-full">
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Container>
        </main>
        <footer className="border-t border-gray-200 bg-white py-3 text-center text-xs text-gray-500">
          Build {BUILD_VERSION}
        </footer>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <RecruiterHeaderNew 
          onScheduleInterview={() => console.log('Schedule interview')}
          onCreatePosition={() => console.log('Create position')}
        />
        <main className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex-1 w-full">
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Alert severity="warning">Profile not found</Alert>
          </Container>
        </main>
        <footer className="border-t border-gray-200 bg-white py-3 text-center text-xs text-gray-500">
          Build {BUILD_VERSION}
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <RecruiterHeaderNew 
        onScheduleInterview={() => console.log('Schedule interview')}
        onCreatePosition={() => console.log('Create position')}
      />
      
      <main className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex-1 w-full">
        <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Profile Header with contact info and social links */}
      <ProfileHeader profile={profile} />

      {/* Summary Section */}
      <ProfileSummary summary={profile.summary} />

      {/* Skills Section */}
      <ProfileSkills skills={profile.skills} />

      {/* Work Experience Section */}
      <ProfileWorkExperience workExperience={profile.work_experience} />

      {/* Education Section */}
      <ProfileEducation education={profile.education_details} />

      {/* Certifications Section */}
      <ProfileCertifications certifications={profile.certifications} />

      {/* Awards Section */}
      <ProfileAwards awards={profile.awards} />

      {/* Languages Section */}
      <ProfileLanguages languages={profile.languages} />

      {/* Preferred Locations */}
      <ProfilePreferredLocations locations={profile.preferred_locations} />
        </Container>
      </main>

      <footer className="border-t border-gray-200 bg-white py-3 text-center text-xs text-gray-500">
        Build {BUILD_VERSION}
      </footer>
    </div>
  );
}